import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft as ArrowLeftIcon, Printer as PrinterIcon } from 'lucide-react';
import pacientesService from '../../services/pacientesService';
import medicoVfpService, { PacienteVfp, DoctorInfo } from '../../services/medicoVfpService';
import { FieldPosition } from '../../components/receta/RecipePrintCanvas';
import OrdenBeneficenciaCanvas, {
  OrdenBeneficenciaFieldKey,
  DEFAULT_ORDEN_BENEFICENCIA_POSITIONS,
  ORDEN_BENEFICENCIA_FIELD_KEYS,
} from '../../components/receta/OrdenBeneficenciaCanvas';
import OrdenAngelesCanvas, {
  OrdenAngelesFieldKey,
  DEFAULT_ORDEN_ANGELES_POSITIONS,
  ORDEN_ANGELES_FIELD_KEYS,
} from '../../components/receta/OrdenAngelesCanvas';

type OrdenFormat = 'beneficencia' | 'angeles';
type AnyOrdenFieldKey = OrdenBeneficenciaFieldKey | OrdenAngelesFieldKey;

function formatMexicoCityDate(now: Date) {
  const tz = 'America/Mexico_City';
  const parts = new Intl.DateTimeFormat('es-MX', {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const month = get('month').replace('.', '').toUpperCase();
  return `${get('day')} ${month} ${get('year')}`;
}

function formatMexicoCityMonthYear(now: Date) {
  const tz = 'America/Mexico_City';
  const parts = new Intl.DateTimeFormat('es-MX', {
    timeZone: tz,
    year: 'numeric',
    month: 'long',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const month = get('month');
  const monthCap = month.charAt(0).toUpperCase() + month.slice(1);
  return `${monthCap} , ${get('year')}`;
}

function calcularEdadDetallada(nacimiento?: Date | string | null) {
  if (!nacimiento) return { anios: 0, meses: 0, dias: 0 };
  const birth = new Date(nacimiento);
  if (Number.isNaN(birth.getTime())) return { anios: 0, meses: 0, dias: 0 };

  const today = new Date();
  let y = today.getFullYear() - birth.getFullYear();
  let m = today.getMonth() - birth.getMonth();
  let d = today.getDate() - birth.getDate();

  if (d < 0) {
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    d += prevMonth.getDate();
    m -= 1;
  }
  if (m < 0) {
    m += 12;
    y -= 1;
  }
  return { anios: Math.max(0, y), meses: Math.max(0, m), dias: Math.max(0, d) };
}

export default function MedicoVfpOrdenPrintPage() {
  const navigate = useNavigate();
  const { state } = useLocation() as {
    state?: {
      ordenTexto?: string;
      formato?: string;
    };
  };
  const { clave } = useParams<{ clave: string }>();
  const [loading, setLoading] = useState(true);
  const [paciente, setPaciente] = useState<PacienteVfp | null>(null);
  const [doctorInfo, setDoctorInfo] = useState<DoctorInfo | null>(null);
  const [ordenFormat, setOrdenFormat] = useState<OrdenFormat>(() => {
    if (state?.formato === 'beneficencia' || state?.formato === 'angeles') return state.formato;
    const saved = localStorage.getItem('ordenFormat');
    return (saved === 'beneficencia' || saved === 'angeles') ? saved : 'beneficencia';
  });

  // Layout state (formatos orden_beneficencia / orden_angeles en receta_layout)
  const [editMode, setEditMode] = useState(false);
  const [selectedField, setSelectedField] = useState<AnyOrdenFieldKey | null>(null);
  const [fieldPositions, setFieldPositions] = useState<Partial<Record<string, FieldPosition>>>({});
  const [fieldDimensions, setFieldDimensions] = useState<
    Partial<Record<string, { width: number; height: number }>>
  >({});
  const [initialLayout, setInitialLayout] = useState<Record<string, FieldPosition>>({});

  const layoutFormato = `orden_${ordenFormat}`;

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        if (!clave) return;

        const [pacienteData, doctor] = await Promise.all([
          medicoVfpService.getPacienteVfp(Number(clave)),
          medicoVfpService.getDoctorInfo().catch(() => null),
        ]);

        setPaciente(pacienteData);
        setDoctorInfo(doctor);
      } catch (error) {
        console.error('Error al cargar paciente VFP:', error);
        toast.error('Error al cargar paciente');
        navigate('/medico-vfp/pacientes');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [clave, navigate]);

  // Cargar/guardar layout
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const layouts = await pacientesService.getRecetaLayout(layoutFormato);

        const layoutMap = layouts.reduce<Partial<Record<string, FieldPosition>>>((acc, layout) => {
          acc[layout.campo] = {
            top: layout.top,
            left: layout.left,
            ...(layout.width != null ? { width: layout.width } : {}),
          };
          return acc;
        }, {});

        const allKeys = ordenFormat === 'beneficencia'
          ? ORDEN_BENEFICENCIA_FIELD_KEYS
          : ORDEN_ANGELES_FIELD_KEYS;
        const defaultPositions: Record<string, FieldPosition> = ordenFormat === 'beneficencia'
          ? DEFAULT_ORDEN_BENEFICENCIA_POSITIONS
          : DEFAULT_ORDEN_ANGELES_POSITIONS;

        const merged = allKeys.reduce<Record<string, FieldPosition>>((acc, key) => {
          const base = defaultPositions[key];
          const override = layoutMap[key];
          acc[key] = { ...base, ...(override ?? {}) };
          return acc;
        }, {});

        const overrides = allKeys.reduce<Partial<Record<string, FieldPosition>>>((acc, key) => {
          const base = defaultPositions[key];
          const next = merged[key];
          if (next.top !== base.top || next.left !== base.left || (next.width ?? null) !== (base.width ?? null)) {
            acc[key] = { ...next };
          }
          return acc;
        }, {});

        setInitialLayout(merged);
        setFieldPositions(overrides);
      } catch (e) {
        console.error('Error al cargar layout de orden:', e);
      }
    };
    loadLayout();
  }, [ordenFormat, layoutFormato]);

  const now = useMemo(() => new Date(), []);
  const fechaMx = useMemo(() => formatMexicoCityDate(now), [now]);
  const fechaMesAnio = useMemo(() => formatMexicoCityMonthYear(now), [now]);
  const edad = useMemo(() => calcularEdadDetallada(paciente?.nacimiento ?? null), [paciente]);

  const PX_TO_CANVAS_UNITS = 2.64583333;
  const BASE_KEY_STEP = Math.max(1, Math.round(PX_TO_CANVAS_UNITS));

  const handleFieldBoundsChange = useCallback(
    (field: AnyOrdenFieldKey, size: { width: number; height: number }) => {
      const widthUnits = Math.max(0, Math.round(size.width * PX_TO_CANVAS_UNITS));
      const heightUnits = Math.max(0, Math.round(size.height * PX_TO_CANVAS_UNITS));
      setFieldDimensions((prev) => {
        const existing = prev[field];
        if (existing && existing.width === widthUnits && existing.height === heightUnits) return prev;
        return { ...prev, [field]: { width: widthUnits, height: heightUnits } };
      });
    },
    [PX_TO_CANVAS_UNITS],
  );

  const updateFieldPosition = (field: string, updater: (current: FieldPosition) => FieldPosition) => {
    setFieldPositions((prev) => {
      const defaultPositions: Record<string, FieldPosition> = ordenFormat === 'beneficencia'
        ? DEFAULT_ORDEN_BENEFICENCIA_POSITIONS
        : DEFAULT_ORDEN_ANGELES_POSITIONS;

      const base = defaultPositions[field];
      if (!base) {
        console.warn(`No se encontró posición base para el campo: ${field}`);
        return prev;
      }

      const current = { ...base, ...prev[field] };
      const updated = updater(current);
      const sanitized: FieldPosition = { top: Math.round(updated.top), left: Math.round(updated.left) };
      const sameAsDefault = sanitized.top === base.top && sanitized.left === base.left;
      if (sameAsDefault) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: sanitized };
    });
  };

  useEffect(() => {
    if (!editMode) return;
    const CANVAS_WIDTH = 1800;
    const CANVAS_HEIGHT = 3071;
    const handler = (event: KeyboardEvent) => {
      if (!selectedField) return;
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(event.key)) return;
      event.preventDefault();
      const multiplier = event.shiftKey ? 5 : 1;
      const delta = BASE_KEY_STEP * multiplier;
      updateFieldPosition(selectedField, (current) => {
        const dimension = fieldDimensions[selectedField];
        const defaultPositions: Record<string, FieldPosition> = ordenFormat === 'beneficencia'
          ? DEFAULT_ORDEN_BENEFICENCIA_POSITIONS
          : DEFAULT_ORDEN_ANGELES_POSITIONS;
        const fallbackWidth = current.width ?? defaultPositions[selectedField as string]?.width ?? 0;
        const width = dimension?.width ?? fallbackWidth;
        const height = dimension?.height ?? 0;
        let nextTop = current.top;
        let nextLeft = current.left;
        switch (event.key) {
          case 'ArrowUp':
            nextTop -= delta;
            break;
          case 'ArrowDown':
            nextTop += delta;
            break;
          case 'ArrowLeft':
            nextLeft -= delta;
            break;
          case 'ArrowRight':
            nextLeft += delta;
            break;
        }
        const maxTop = height > 0 ? Math.max(0, CANVAS_HEIGHT - height) : CANVAS_HEIGHT;
        const clampedTop = Math.min(Math.max(nextTop, 0), maxTop);
        const maxLeft = width > 0 ? Math.max(0, CANVAS_WIDTH - width) : CANVAS_WIDTH;
        const clampedLeft = Math.min(Math.max(nextLeft, 0), maxLeft);
        return { ...current, top: clampedTop, left: clampedLeft };
      });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editMode, selectedField, fieldDimensions, ordenFormat, updateFieldPosition, BASE_KEY_STEP]);

  const handleToggleEdit = () => {
    setEditMode((prev) => {
      if (prev) setSelectedField(null);
      return !prev;
    });
  };

  const handleResetPositions = () => {
    setFieldPositions({});
    setSelectedField(null);
    toast.success('Posiciones restablecidas');
  };

  const handleSavePositions = async () => {
    try {
      const allKeys = ordenFormat === 'beneficencia'
        ? ORDEN_BENEFICENCIA_FIELD_KEYS
        : ORDEN_ANGELES_FIELD_KEYS;
      const defaultPositions: Record<string, FieldPosition> = ordenFormat === 'beneficencia'
        ? DEFAULT_ORDEN_BENEFICENCIA_POSITIONS
        : DEFAULT_ORDEN_ANGELES_POSITIONS;

      const mergedLayout = allKeys.reduce<Record<string, FieldPosition>>((acc, key) => {
        const base = defaultPositions[key];
        const override = fieldPositions[key];
        acc[key] = { ...base, ...(override ?? {}) };
        return acc;
      }, {});

      const hasChanges = allKeys.some((key) => {
        const next = mergedLayout[key];
        const prev = initialLayout[key] ?? defaultPositions[key];
        return next.top !== prev.top || next.left !== prev.left || (next.width ?? null) !== (prev.width ?? null);
      });
      if (!hasChanges) {
        toast.error('No hay cambios para guardar');
        return;
      }
      const positions = allKeys.map((campo) => ({
        campo,
        top: mergedLayout[campo].top,
        left: mergedLayout[campo].left,
        width: mergedLayout[campo].width,
      }));
      await pacientesService.updateRecetaLayout({ formato: layoutFormato, positions });
      toast.success('Posiciones guardadas exitosamente');
      setInitialLayout(mergedLayout);
    } catch (error) {
      console.error('Error al guardar posiciones:', error);
      toast.error('Error al guardar posiciones');
    }
  };

  const handlePrint = () => {
    setEditMode(false);
    setSelectedField(null);
    window.print();
  };

  const handleFormatChange = (format: OrdenFormat) => {
    setOrdenFormat(format);
    localStorage.setItem('ordenFormat', format);
  };

  const handleVolver = () => {
    navigate(`/medico-vfp/${clave}/receta`, {
      state: {
        restoreOrden: true,
        ordenTexto: state?.ordenTexto ?? '',
        ordenFormat,
      },
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!paciente) return null;

  const ordenTexto = (state?.ordenTexto ?? '').trim();
  const patientName = `${paciente.nombre ?? ''} ${paciente.apellido1 ?? ''}`
    .trim()
    .toUpperCase();
  const doctorName = doctorInfo?.nombre ?? 'Dr. José Luis Cadena Santillana';

  return (
    <div className="max-w-6xl mx-auto print:max-w-none print:mx-0">
      {/* Controles - no se imprimen */}
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6 print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={handleVolver}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Volver
          </button>

          {/* Selector de formato */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Formato:</label>
            <select
              value={ordenFormat}
              onChange={(e) => handleFormatChange(e.target.value as OrdenFormat)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="beneficencia">Beneficencia Española</option>
              <option value="angeles">Hospital Ángeles</option>
            </select>
          </div>

          <button
            onClick={handleToggleEdit}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
              editMode
                ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                : 'text-blue-700 border-blue-200 hover:bg-blue-50'
            }`}
          >
            {editMode ? 'Salir modo edición' : 'Editar posiciones'}
          </button>
          {editMode && (
            <>
              <button
                onClick={handleSavePositions}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                Guardar posiciones
              </button>
              <button
                onClick={handleResetPositions}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Restablecer posiciones
              </button>
            </>
          )}
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PrinterIcon className="w-5 h-5" />
          Imprimir Orden
        </button>
      </div>

      {/* Vista previa */}
      <div className="print:hidden">
        {editMode && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 text-sm space-y-1">
            <p className="font-semibold text-blue-900">Modo edición activo</p>
            <p>Selecciona un campo y usa las flechas para moverlo 1px.</p>
            <p>Mantén Shift para mover de 5px en 5px. Pulsa "Restablecer posiciones" para volver al diseño original.</p>
          </div>
        )}
        <div className="recipe-preview-shell">
          {ordenFormat === 'beneficencia' ? (
            <OrdenBeneficenciaCanvas
              patientName={patientName}
              consultDate={fechaMx}
              ordenTexto={ordenTexto}
              doctorName={doctorName}
              editMode={editMode}
              selectedField={selectedField as any}
              onSelectField={setSelectedField as any}
              fieldPositions={fieldPositions as any}
              onFieldBoundsChange={handleFieldBoundsChange as any}
            />
          ) : (
            <OrdenAngelesCanvas
              patientName={patientName}
              ageYears={String(edad.anios)}
              ordenTexto={ordenTexto}
              footerDate={fechaMesAnio}
              doctorName={doctorName.toUpperCase()}
              editMode={editMode}
              selectedField={selectedField as any}
              onSelectField={setSelectedField as any}
              fieldPositions={fieldPositions as any}
              onFieldBoundsChange={handleFieldBoundsChange as any}
            />
          )}
        </div>
      </div>

      {/* Versión para impresión */}
      <div className="hidden print:block">
        {ordenFormat === 'beneficencia' ? (
          <OrdenBeneficenciaCanvas
            patientName={patientName}
            consultDate={fechaMx}
            ordenTexto={ordenTexto}
            doctorName={doctorName}
            fieldPositions={fieldPositions as any}
            onFieldBoundsChange={handleFieldBoundsChange as any}
          />
        ) : (
          <OrdenAngelesCanvas
            patientName={patientName}
            ageYears={String(edad.anios)}
            ordenTexto={ordenTexto}
            footerDate={fechaMesAnio}
            doctorName={doctorName.toUpperCase()}
            fieldPositions={fieldPositions as any}
            onFieldBoundsChange={handleFieldBoundsChange as any}
          />
        )}
      </div>
    </div>
  );
}
